import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle,
    Check,
    ChevronDown,
    ChevronUp,
    Loader2,
    Pencil,
    Plus,
    Search,
    Trash2,
    User,
    Users,
    UsersRound,
    X,
} from 'lucide-react';
import {
    fetchCollection,
    saveWikiGroup,
    deleteWikiGroup,
} from '../../../services/api';

const GROUPS_URL = '/decoupled-api/group/wiki/collection?display=decoupled';
const USERS_URL = '/decoupled-api/user/user/collection?display=decoupled';

function normalizeGroup(raw) {
    const d = raw?.data ?? raw;
    return {
        id: typeof d.id === 'string' ? parseInt(d.id, 10) : d.id,
        name: d.name || '',
        description: d.description || '',
        memberIds: Array.isArray(d.memberIds) ? d.memberIds.map(Number) : [],
    };
}

function normalizeUser(raw) {
    const d = raw?.data ?? raw;
    return {
        id: typeof d.id === 'string' ? parseInt(d.id, 10) : d.id,
        name: d.name || d.label || '',
    };
}

function DeleteConfirm({ groupName, onConfirm, onCancel, loading }) {
    return (
        <div id="doc-settings-groups-delete-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle size={20} className="text-red-500" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900">Supprimer le groupe</h3>
                        <p className="text-sm text-stone-500 mt-1">
                            Supprimer <strong className="text-stone-700">{groupName}</strong> ? Les espaces liés perdront cette association.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onCancel} disabled={loading} className="btn btn-secondary">
                        Annuler
                    </button>
                    <button type="button" onClick={onConfirm} disabled={loading} className="btn btn-danger disabled:opacity-50 flex items-center gap-2">
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    );
}

function UserAutocomplete({ allUsers, selectedIds, onAdd, placeholder }) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    const normalizedQuery = query.toLowerCase().trim();
    const filtered = useMemo(() => {
        if (!normalizedQuery) return allUsers.filter((u) => !selectedIds.includes(u.id));
        return allUsers.filter(
            (u) => !selectedIds.includes(u.id) && u.name.toLowerCase().includes(normalizedQuery),
        );
    }, [allUsers, selectedIds, normalizedQuery]);

    return (
        <div ref={containerRef} className="relative">
            <div
                className={`flex items-center gap-1.5 border rounded-lg bg-white transition-all cursor-text ${
                    isOpen ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-stone-200 hover:border-stone-300'
                }`}
                onClick={() => setIsOpen(true)}
            >
                <Search size={12} className="ml-2.5 text-stone-400 flex-shrink-0" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                    className="flex-1 py-2 pr-2 text-sm outline-none bg-transparent placeholder:text-stone-400 min-w-0"
                    aria-label="Rechercher un utilisateur"
                />
            </div>
            {isOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto doc-sidebar-scroll">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-stone-400 italic text-center">
                            {allUsers.length === 0 ? 'Chargement…' : 'Aucun utilisateur trouvé'}
                        </div>
                    ) : (
                        filtered.map((user) => (
                            <button
                                key={user.id}
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onAdd(user.id);
                                    setQuery('');
                                    setIsOpen(false);
                                }}
                                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-left text-stone-600 hover:bg-stone-50 transition-colors"
                            >
                                <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                                    <User size={11} className="text-stone-400" />
                                </div>
                                <span className="truncate">{user.name}</span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function MemberChip({ user, onRemove }) {
    return (
        <span className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 text-xs bg-stone-100 text-stone-700 rounded-full border border-stone-200">
            <div className="w-4 h-4 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                <User size={8} className="text-stone-500" />
            </div>
            <span className="truncate max-w-[120px]">{user.name}</span>
            <button
                type="button"
                onClick={() => onRemove(user.id)}
                className="p-0.5 rounded-full hover:bg-stone-200 text-stone-400 hover:text-red-500 transition-colors"
                aria-label={`Retirer ${user.name}`}
            >
                <X size={10} />
            </button>
        </span>
    );
}

function GroupDetail({ group, allUsers, onSaveMembers, saving }) {
    const [localMemberIds, setLocalMemberIds] = useState(group.memberIds);
    const [dirty, setDirty] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        setLocalMemberIds(group.memberIds);
        setDirty(false);
        setSuccess(false);
    }, [group.id, group.memberIds]);

    const memberUsers = useMemo(
        () => localMemberIds.map((id) => allUsers.find((u) => u.id === id)).filter(Boolean),
        [localMemberIds, allUsers],
    );

    const handleAdd = useCallback((userId) => {
        setLocalMemberIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
        setDirty(true);
        setSuccess(false);
    }, []);

    const handleRemove = useCallback((userId) => {
        setLocalMemberIds((prev) => prev.filter((id) => id !== userId));
        setDirty(true);
        setSuccess(false);
    }, []);

    const handleSave = useCallback(async () => {
        await onSaveMembers(group.id, localMemberIds);
        setDirty(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
    }, [group.id, localMemberIds, onSaveMembers]);

    return (
        <div className="px-4 py-3 bg-stone-50/60 border-t border-stone-100 space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Membres ({localMemberIds.length})
                </span>
                <div className="flex items-center gap-2">
                    {success && (
                        <span className="inline-flex items-center gap-1 text-2xs text-emerald-600 font-medium">
                            <Check size={10} /> Sauvegardé
                        </span>
                    )}
                    {dirty && (
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="btn btn-primary btn-sm disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                            Enregistrer
                        </button>
                    )}
                </div>
            </div>

            <UserAutocomplete
                allUsers={allUsers}
                selectedIds={localMemberIds}
                onAdd={handleAdd}
                placeholder="Ajouter un membre…"
            />

            {memberUsers.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {memberUsers.map((user) => (
                        <MemberChip key={user.id} user={user} onRemove={handleRemove} />
                    ))}
                </div>
            ) : (
                <p className="text-xs text-stone-400 italic text-center py-2">Aucun membre dans ce groupe.</p>
            )}
        </div>
    );
}

function GroupRow({ group, allUsers, onEdit, onDelete, onToggleExpand, isExpanded, onSaveMembers, savingMembers, editingId, editState, setEditState, onEditSave, onEditCancel, savingId }) {
    const isEditing = editingId === group.id;

    if (isEditing) {
        return (
            <div id={`doc-settings-group-edit-${group.id}`} className="px-4 py-3 bg-blue-50/40">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                        type="text"
                        value={editState.name}
                        onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onEditSave();
                            if (e.key === 'Escape') onEditCancel();
                        }}
                        placeholder="Nom *"
                        className="input flex-1"
                        autoFocus
                    />
                    <input
                        type="text"
                        value={editState.description}
                        onChange={(e) => setEditState((s) => ({ ...s, description: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onEditSave();
                            if (e.key === 'Escape') onEditCancel();
                        }}
                        placeholder="Description (optionnel)"
                        className="input flex-1"
                    />
                    <div className="flex items-center gap-1.5 self-end sm:self-auto">
                        <button
                            type="button"
                            onClick={onEditSave}
                            disabled={savingId === group.id || !editState.name.trim()}
                            className="btn btn-primary btn-sm disabled:opacity-50 inline-flex items-center gap-1.5"
                            aria-label="Enregistrer"
                        >
                            {savingId === group.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                            <span className="sm:hidden">Enregistrer</span>
                        </button>
                        <button type="button" onClick={onEditCancel} className="p-2 rounded-lg text-stone-400 hover:bg-stone-200 hover:text-stone-600 transition-colors" aria-label="Annuler">
                            <X size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const ExpandIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
        <div id={`doc-settings-group-${group.id}`}>
            <div className="flex items-center gap-2.5 px-4 py-3 group hover:bg-stone-50 transition-colors">
                <button
                    type="button"
                    onClick={() => onToggleExpand(group.id)}
                    className="p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors flex-shrink-0"
                    aria-label={isExpanded ? 'Réduire' : 'Développer'}
                >
                    <ExpandIcon size={14} />
                </button>

                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onToggleExpand(group.id)}>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-stone-800 font-medium truncate">{group.name}</span>
                        <span className="inline-flex items-center gap-1 text-2xs text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            <Users size={10} />
                            {group.memberIds.length}
                        </span>
                    </div>
                    {group.description && (
                        <p className="text-xs text-stone-400 truncate mt-0.5">{group.description}</p>
                    )}
                </div>

                <div className="flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button type="button" onClick={() => onEdit(group)} className="p-1.5 rounded-md text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" aria-label={`Modifier ${group.name}`}>
                        <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => onDelete(group)} className="p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label={`Supprimer ${group.name}`}>
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            {isExpanded && (
                <GroupDetail
                    group={group}
                    allUsers={allUsers}
                    onSaveMembers={onSaveMembers}
                    saving={savingMembers === group.id}
                />
            )}
        </div>
    );
}

export default function DocSettingsGroups() {
    const [groups, setGroups] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editState, setEditState] = useState({ name: '', description: '' });
    const [savingId, setSavingId] = useState(null);

    const [expandedId, setExpandedId] = useState(null);
    const [savingMembers, setSavingMembers] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchGroups = useCallback(async () => {
        try {
            const raw = await fetchCollection(GROUPS_URL, true);
            const items = (Array.isArray(raw) ? raw : []).map(normalizeGroup);
            setGroups(items);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        fetch(USERS_URL, { credentials: 'include' })
            .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
            .then((data) => {
                if (cancelled) return;
                const users = (Array.isArray(data) ? data : [])
                    .filter((item) => item?.data ?? item?.id != null)
                    .map(normalizeUser)
                    .filter((u) => u.id && u.name);
                setAllUsers(users);
            })
            .catch(() => { if (!cancelled) setAllUsers([]); });
        return () => { cancelled = true; };
    }, []);

    const handleCreate = useCallback(async () => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        setCreating(true);
        setError('');
        try {
            await saveWikiGroup({ name: trimmed, description: newDescription.trim() });
            setNewName('');
            setNewDescription('');
            await fetchGroups();
        } catch (err) {
            setError(err.message);
        } finally {
            setCreating(false);
        }
    }, [newName, newDescription, fetchGroups]);

    const handleEditStart = useCallback((group) => {
        setEditingId(group.id);
        setEditState({ name: group.name, description: group.description || '' });
    }, []);

    const handleEditSave = useCallback(async () => {
        if (!editingId) return;
        const trimmed = editState.name.trim();
        if (!trimmed) return;
        setSavingId(editingId);
        setError('');
        try {
            await saveWikiGroup({ id: editingId, name: trimmed, description: editState.description.trim() });
            setEditingId(null);
            await fetchGroups();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingId(null);
        }
    }, [editingId, editState, fetchGroups]);

    const handleEditCancel = useCallback(() => { setEditingId(null); }, []);

    const handleToggleExpand = useCallback((groupId) => {
        setExpandedId((prev) => (prev === groupId ? null : groupId));
    }, []);

    const handleSaveMembers = useCallback(async (groupId, memberIds) => {
        setSavingMembers(groupId);
        setError('');
        try {
            await saveWikiGroup({ id: groupId, memberIds });
            await fetchGroups();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingMembers(null);
        }
    }, [fetchGroups]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setError('');
        try {
            await deleteWikiGroup(deleteTarget.id);
            setDeleteTarget(null);
            if (expandedId === deleteTarget.id) setExpandedId(null);
            await fetchGroups();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleting(false);
        }
    }, [deleteTarget, expandedId, fetchGroups]);

    if (loading) {
        return (
            <div id="doc-settings-groups" className="flex items-center justify-center py-16 gap-3">
                <Loader2 size={20} className="animate-spin text-stone-400" />
                <span className="text-sm text-stone-400">Chargement des groupes…</span>
            </div>
        );
    }

    return (
        <div id="doc-settings-groups">
            <div id="doc-settings-groups-intro" className="mb-6">
                <h2 className="text-lg font-semibold text-stone-900 mb-1">Groupes wiki</h2>
                <p className="text-sm text-stone-500">
                    Gérez les groupes wiki de votre espace documentaire. Les groupes permettent d'attribuer des rôles et des accès par ensemble d'utilisateurs.
                </p>
            </div>

            {error && (
                <div id="doc-settings-groups-error" className="mb-4 px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0" />
                    <span className="flex-1">{error}</span>
                    <button type="button" onClick={() => setError('')} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0" aria-label="Fermer l'erreur">
                        <X size={12} />
                    </button>
                </div>
            )}

            <div id="doc-settings-groups-form" className="bg-white border border-stone-200 rounded-xl mb-6">
                <div className="px-5 py-3.5 border-b border-stone-100 flex items-center gap-2">
                    <Plus size={14} className="text-stone-400" />
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Nouveau groupe</h3>
                </div>
                <div className="p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label htmlFor="new-group-name" className="block text-xs font-medium text-stone-500 mb-1.5">
                                Nom <span className="text-red-400">*</span>
                            </label>
                            <input
                                id="new-group-name"
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                placeholder="Ex : Tout le monde, Direction…"
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="new-group-desc" className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
                            <input
                                id="new-group-desc"
                                type="text"
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                placeholder="Description courte (optionnel)"
                                className="input"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating || !newName.trim()}
                            className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        >
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            <span>Ajouter le groupe</span>
                        </button>
                    </div>
                </div>
            </div>

            <div id="doc-settings-groups-list" className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3.5 border-b border-stone-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <UsersRound size={14} className="text-stone-400" />
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                            Groupes existants
                        </span>
                    </div>
                    <span className="text-xs text-stone-400 tabular-nums">
                        {groups.length} groupe{groups.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {groups.length === 0 ? (
                    <div id="doc-settings-groups-empty" className="px-6 py-14 text-center">
                        <UsersRound size={32} className="text-stone-200 mx-auto mb-3" />
                        <p className="text-sm text-stone-400 font-medium">Aucun groupe créé</p>
                        <p className="text-xs text-stone-400 mt-1">Utilisez le formulaire ci-dessus pour commencer.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100">
                        {groups.map((group) => (
                            <GroupRow
                                key={group.id}
                                group={group}
                                allUsers={allUsers}
                                onEdit={handleEditStart}
                                onDelete={setDeleteTarget}
                                onToggleExpand={handleToggleExpand}
                                isExpanded={expandedId === group.id}
                                onSaveMembers={handleSaveMembers}
                                savingMembers={savingMembers}
                                editingId={editingId}
                                editState={editState}
                                setEditState={setEditState}
                                onEditSave={handleEditSave}
                                onEditCancel={handleEditCancel}
                                savingId={savingId}
                            />
                        ))}
                    </div>
                )}
            </div>

            {deleteTarget && (
                <DeleteConfirm
                    groupName={deleteTarget.name}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
